/** Trip-update (delay) service — fetches GTFS-RT trip updates via the Hono proxy. */
import GtfsRealtimeBindings from "gtfs-realtime-bindings";
import { apiGetRaw } from "./api-client";
import type { RealtimeMode } from "./gtfs-realtime";

// Cache: Map<trip_id, delay_seconds> per mode, with a 60s TTL.
const delayCache = new Map<string, { data: Map<string, number>; ts: number }>();

function s(v: any): string { return v == null ? "" : typeof v === "string" ? v : String(v); }
function n(v: any): number { if (typeof v === "number") return v; if (v && typeof v.low === "number") return v.low; if (v && typeof v.toNumber === "function") return v.toNumber(); return Number(v) || 0; }
function arr(v: any): any[] { return Array.isArray(v) ? v : v ? [v] : []; }

/**
 * Fetch trip delay info for a given mode.
 * Returns a Map from trip_id → delay (in seconds, negative = early, positive = late).
 * Falls back to an empty map for ferry modes (TfNSW 404s), cached 60 s.
 */
const inFlight = new Map<string, Promise<Map<string, number>>>();

export async function fetchTripDelays(mode: RealtimeMode): Promise<Map<string, number>> {
  const cached = delayCache.get(mode);
  if (cached && Date.now() - cached.ts < 60_000) return cached.data;

  // De-duplicate in-flight requests for the same mode
  const pending = inFlight.get(mode);
  if (pending) return pending;

  const promise = (async () => {
    try {
      const buffer = await apiGetRaw("/gtfs/realtime/" + mode);
      const feed = GtfsRealtimeBindings.transit_realtime.FeedMessage.decode(new Uint8Array(buffer));
      const json = feed.toJSON();
      const map = new Map<string, number>();
      for (const e of arr(json.entity)) {
        if (!e.tripUpdate) continue;
        const tid = s(e.tripUpdate.trip?.tripId);
        if (!tid) continue;
        let maxDelay = 0;
        for (const stu of arr(e.tripUpdate.stopTimeUpdate)) {
          const d = n(stu.departure?.delay) || n(stu.arrival?.delay);
          if (Math.abs(d) > Math.abs(maxDelay)) maxDelay = d;
        }
        map.set(tid, maxDelay);
      }
      delayCache.set(mode, { data: map, ts: Date.now() });
      return map;
    } catch {
      // Ferry trip-updates 404 — return empty, cached for 60s so we don't retry every poll
      const empty = new Map<string, number>();
      delayCache.set(mode, { data: empty, ts: Date.now() });
      return empty;
    }
  })();

  inFlight.set(mode, promise);
  try { return await promise; } finally { inFlight.delete(mode); }
}

/**
 * Fetch per-trip, per-stop delay info for a given mode.
 * Returns a Map from trip_id → Map<stop_id, delay_seconds>.
 * Cached 60 s like fetchTripDelays.
 */
const stopDelayCache = new Map<string, { data: Map<string, Map<string, number>>; ts: number }>();
const stopDelayInFlight = new Map<string, Promise<Map<string, Map<string, number>>>>();

export async function fetchTripStopDelays(mode: RealtimeMode): Promise<Map<string, Map<string, number>>> {
  const cached = stopDelayCache.get(mode);
  if (cached && Date.now() - cached.ts < 60_000) return cached.data;

  const pending = stopDelayInFlight.get(mode);
  if (pending) return pending;

  const promise = (async () => {
    const map = new Map<string, Map<string, number>>();
    try {
      const buffer = await apiGetRaw("/gtfs/realtime/" + mode);
      const feed = GtfsRealtimeBindings.transit_realtime.FeedMessage.decode(new Uint8Array(buffer));
      const json = feed.toJSON();
      for (const e of arr(json.entity)) {
        if (!e.tripUpdate) continue;
        const tid = s(e.tripUpdate.trip?.tripId);
        if (!tid) continue;
        const perStop = new Map<string, number>();
        for (const stu of arr(e.tripUpdate.stopTimeUpdate)) {
          const stopId = s(stu.stopId);
          if (!stopId) continue;
          const d = n(stu.departure?.delay) || n(stu.arrival?.delay);
          perStop.set(stopId, d);
        }
        if (perStop.size) map.set(tid, perStop);
      }
    } catch {
      // Some modes 404 — return empty, cached briefly.
    }
    stopDelayCache.set(mode, { data: map, ts: Date.now() });
    return map;
  })();

  stopDelayInFlight.set(mode, promise);
  try { return await promise; } finally { stopDelayInFlight.delete(mode); }
}
